import { useRouter } from 'expo-router'
import * as React from 'react'
import { useState } from 'react'
import { Dialog, Paragraph } from 'react-native-paper'
import { mvs, ScaledSheet } from 'react-native-size-matters'
import CacheableImage from '~components/CacheableImage'
import IconChatLarge from '~components/IconChatLarge'
import { Action, NavigationItem } from '~components/NavigationItem'
import { View } from '~components/Themed'
import { RecentChat, Role } from '~redux/recent/types'
import ChatService from '~services/chat'
import { Theme } from '~theme'
import { useTheme } from '~theme/ThemeManager'
import { ExistingChat } from '~types'
import opacity from '~utils/opacity'
import parseChatPath from '~utils/parseChatPath'
import { AlertConfirmation } from './AlertConfirmation'
import { useTypedDispatch } from '~redux'
import { removeRecentChat } from '~redux/recent'

export interface ChatNavigationItemProps {
  chat: ExistingChat | RecentChat
  role: Role
  title?: string
  isReply?: boolean
  isDashboard?: boolean
}

export function ChatNavigationItem({
  chat,
  role,
  title = chat.name,
  isReply = false,
  isDashboard = false,
}: ChatNavigationItemProps): React.JSX.Element {
  const { theme } = useTheme()
  const styles = getStyles(theme)
  const router = useRouter()
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const dispatch = useTypedDispatch()
  const { classId, chatId, userId } = parseChatPath(chat)

  const thumbnail =
    'sections' in chat
      ? Object.values((chat as ExistingChat).sections ?? [])
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((section) => section?.image?.src ?? '')
          .find((src) => src.length !== 0)
      : chat.thumbnail

  const onPress = () => {
    if (classId === undefined || chatId === undefined) {
      return
    }

    if (isDashboard) {
      router.navigate(`/folders/${classId}`, { withAnchor: true })
      setTimeout(
        () =>
          router.navigate({
            pathname: '/folders/[folderId]/chats/[chatId]',
            params: {
              folderId: classId,
              chatId,
              userId,
            },
          }),
        100,
      )
    } else {
      router.navigate(
        {
          pathname: '/folders/[folderId]/chats/[chatId]',
          params: {
            folderId: classId,
            chatId,
            userId,
          },
        },
        { withAnchor: true },
      )
    }
  }

  const canDelete = (): boolean => {
    return role === 'teacher' || (role === 'student' && isReply)
  }

  const deleteChat = async () => {
    if (!canDelete) {
      return
    }

    await ChatService.deleteChat(chat.path)

    const { classId, chatId, userId } = parseChatPath(chat)
    if (classId === undefined || chatId === undefined) {
      return
    }

    if (userId) {
      dispatch(removeRecentChat(classId, chatId, userId))
    }
    dispatch(removeRecentChat(classId, chatId, userId))
  }

  const actions: Action[] = [
    {
      enabled: role === 'teacher',
      name: 'Edit chat settings',
      icon: ['far', 'cog'],
      action: () => {
        router.navigate(
          {
            pathname: '/folders/[folderId]/chats/[chatId]/edit',
            params: {
              folderId: classId,
              chatId,
              userId,
            },
          },
          { withAnchor: false },
        )
      },
    },
    {
      enabled: role === 'teacher' && !isReply && parseChatPath(chat).classId !== undefined,
      name: 'View replies',
      icon: ['far', 'users-class'],
      action: () => {
        router.navigate(
          {
            pathname: '/folders/[folderId]/chats/[chatId]/replies',
            params: {
              folderId: classId,
              chatId,
            },
          },
          { withAnchor: true },
        )
      },
    },
    {
      enabled: role === 'teacher' && !isReply,
      name: 'Upload chat to school templates',
      icon: ['far', 'cloud'],
      action: async () => {
        router.navigate(
          {
            pathname: '/folders/[folderId]/chats/[chatId]/upload',
            params: {
              folderId: classId,
              chatId,
              userId,
            },
          },
          { withAnchor: false },
        )
      },
    },
    {
      enabled: !isDashboard && canDelete(),
      name: 'Delete Chat',
      icon: ['far', 'trash'],
      destructive: true,
      action: () => {
        setShowDeleteConfirmation(true)
      },
    },
  ]

  return (
    <>
      <NavigationItem
        title={title}
        onPress={onPress}
        background={
          thumbnail ? (
            <View style={styles.navigationItemContainer}>
              <CacheableImage
                source={{ uri: thumbnail }}
                style={styles.navigationItemImage}
                contentFit="cover"
                contentPosition={{ top: '25%', left: '50%' }}
                placeholder={
                  <IconChatLarge
                    style={styles.navigationItemPlaceholder}
                    width={mvs(75, 0.6)}
                    height={mvs(75, 0.6)}
                  />
                }
              />
              <View style={styles.navigationItemOverlay} />
            </View>
          ) : (
            <IconChatLarge
              style={styles.navigationItemPlaceholder}
              width={mvs(75, 0.6)}
              height={mvs(75, 0.6)}
            />
          )
        }
        actions={actions}
        backgroundContainerStyle={styles.backgroundContainerStyle}
      />
      {showDeleteConfirmation && (
        <AlertConfirmation
          visible={showDeleteConfirmation}
          setVisible={setShowDeleteConfirmation}
          confirmAction={deleteChat}
        >
          <Dialog.Title>Delete Chat?</Dialog.Title>
          <Dialog.Content>
            <Paragraph>
              Deleting this chat will remove it from your account. If you have previously shared a
              link to this chat, it will no longer work.
            </Paragraph>
          </Dialog.Content>
        </AlertConfirmation>
      )}
    </>
  )
}

const getStyles = (theme: Theme) =>
  ScaledSheet.create({
    navigationItemContainer: {
      flex: 1,
      justifyContent: 'center',
      borderTopLeftRadius: '5@ms',
      borderTopRightRadius: '5@ms',
    },
    navigationItemImage: {
      flex: 1,
      resizeMode: 'cover',
      borderTopLeftRadius: '5@ms',
      borderTopRightRadius: '5@ms',
    },
    navigationItemPlaceholder: {
      alignSelf: 'center',
    },
    navigationItemOverlay: {
      flex: 1,
      borderTopLeftRadius: '5@ms',
      borderTopRightRadius: '5@ms',
      backgroundColor: opacity(theme[500], 0.3),
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    backgroundContainerStyle: {
      borderStartWidth: 0,
      borderTopWidth: 0,
      borderEndWidth: 0,
    },
  })
